/**
 * ServerRebuildModal — Reconstruction intégrale des images workspace (admin).
 *
 * Double confirmation (checkbox "Je comprends") car interruption ~3 min.
 *
 * @spec specs/app/admin/dedicated-servers.md §6.7
 */

import { Alert, Checkbox, Input, Modal } from 'antd';
import { Wrench } from 'lucide-react';
import { useState, type FC } from 'react';
import type { DedicatedServer } from '@/types/dedicatedServer';
import { useDedicatedServerActions } from '../hooks/useDedicatedServerActions';

interface ServerRebuildModalProps {
  server: DedicatedServer;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const MIN_REASON = 10;
const MAX_REASON = 500;

const ServerRebuildModal: FC<ServerRebuildModalProps> = ({
  server,
  open,
  onClose,
  onSuccess,
}) => {
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const { rebuild, isRebuilding } = useDedicatedServerActions(server.id);

  const handleOk = () => {
    if (!confirmed || reason.length < MIN_REASON) return;
    rebuild(reason, {
      onSuccess: () => {
        setReason('');
        setConfirmed(false);
        onClose();
        onSuccess?.();
      },
    });
  };

  const handleCancel = () => {
    setReason('');
    setConfirmed(false);
    onClose();
  };

  const canSubmit = confirmed && reason.length >= MIN_REASON;

  return (
    <Modal
      title={
        <span className="inline-flex items-center gap-2">
          <Wrench className="h-4 w-4 text-amber-500" />
          Reconstruire les images workspace
        </span>
      }
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      okText="Reconstruire"
      cancelText="Annuler"
      okButtonProps={{
        type: 'primary',
        loading: isRebuilding,
        disabled: !canSubmit,
      }}
      destroyOnClose
    >
      <div className="space-y-3">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          L'intégralité des images Docker workspace sera reconstruite sur {server.name}.
        </p>
        <Alert
          type="warning"
          showIcon
          message="Interruption de service ~3 min"
          description="Les workspaces actifs sur ce serveur seront interrompus le temps de la reconstruction."
        />
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
            Raison (obligatoire, {MIN_REASON}–{MAX_REASON} caractères)
          </label>
          <Input.TextArea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ex: ticket #4567 — workspaces_ready=false après setup"
            maxLength={MAX_REASON}
            showCount
            rows={3}
          />
        </div>
        <Checkbox checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)}>
          Je confirme avoir compris les conséquences (interruption ~3 min)
        </Checkbox>
      </div>
    </Modal>
  );
};

export default ServerRebuildModal;
